<template>
  <app-provider ref="appProvider">
    <div
      v-if="showAuthLoader"
      class="auth-boot"
      v-loading="true"
      element-loading-text="Loading..."
    />

    <template v-else-if="isLoggedIn && !isForbidden">
      <admin-shell
        :account="currentAccount"
        @sign-out="handleSignOut"
      >
        <router-view />
      </admin-shell>
    </template>

    <GlanceAuthLogin
      v-else-if="!isLoggedIn"
      :auth="auth"
      :show-google="showGoogleAuth"
      :show-microsoft="showMicrosoftAuth"
      google-label="Sign In With Google"
      microsoft-label="Sign In With Microsoft"
      layout="stack"
    />

    <GlanceAuthForbidden
      v-else-if="isLoggedIn && isForbidden"
      :photo-url="auth.currentUser?.photoURL"
      :email="auth.currentUser?.email"
      message="You do not have permission to view this page.\nContact your manager."
      sign-out-label="Sign out"
      @sign-out="handleSignOut"
    />
  </app-provider>
</template>

<script>
import {
  getAuth,
  onAuthStateChanged,
  signOut,
} from 'firebase/auth'
import { GlanceAuthLogin, GlanceAuthForbidden } from '@glanceai/common/ui'
import { useGlobalError } from '@/composables/useGlobalError'
import AdminShell from '@/components/AdminShell.vue'

export default {
  name: 'AppRoot',
  components: {
    GlanceAuthLogin,
    GlanceAuthForbidden,
    AdminShell,
  },
  setup() {
    const { isForbidden, resetForbidden } = useGlobalError()
    return { isForbidden, resetForbidden }
  },
  data() {
    return {
      isLoggedIn: null,
      sessionVerified: false,
      wasLoggedIn: false,
      auth: getAuth(),
      authProviders: {
        google: process.env.VUE_APP_AUTH_GOOGLE_ENABLED,
        microsoft: process.env.VUE_APP_AUTH_MICROSOFT_ENABLED,
      },
    }
  },
  computed: {
    showGoogleAuth() {
      return this.authProviders.google !== 'false'
    },
    showMicrosoftAuth() {
      return this.authProviders.microsoft === 'true'
    },
    currentAccount() {
      if (!this.isLoggedIn) {
        return null
      }
      const user = this.auth.currentUser
      if (!user) {
        return null
      }
      const provider = user.providerData?.[0]
      return {
        email: user.email || '',
        displayName: user.displayName || '',
        photoUrl: user.photoURL || '',
        emailVerified: Boolean(user.emailVerified),
        providerId: provider?.providerId || user.providerId || '',
      }
    },
    showAuthLoader() {
      if (this.isLoggedIn === null) {
        return true
      }
      return this.isLoggedIn === true && !this.sessionVerified && !this.isForbidden
    },
  },
  provide() {
    return {
      auth: this.auth,
    }
  },
  methods: {
    handleSignOut() {
      this.sessionVerified = false
      this.$refs.appProvider?.resetSessionData?.()
      signOut(this.auth).then(() => {
        this.isLoggedIn = false
        this.wasLoggedIn = false
        this.$router.push({ path: '/' })
      })
    },
    async verifySession() {
      this.sessionVerified = false
      try {
        await this.$refs.appProvider?.prefetchSharedData?.()
      } catch (error) {
        console.error('Session verification failed:', error)
      }
      await this.$nextTick()
      if (this.auth.currentUser && !this.isForbidden) {
        this.sessionVerified = true
      }
    },
  },
  mounted() {
    this.$api.auth = this.auth

    onAuthStateChanged(this.auth, (user) => {
      const loggedIn = !!user
      if (this.wasLoggedIn && !loggedIn) {
        this.$refs.appProvider?.resetSessionData?.()
        this.sessionVerified = false
      }
      this.isLoggedIn = loggedIn
      this.wasLoggedIn = loggedIn
      this.$api.forbidden = false
      this.resetForbidden()

      if (loggedIn) {
        this.$nextTick(() => {
          if (!this.sessionVerified) {
            this.verifySession()
          }
        })
        if (this.$route.name == 'home') {
          this.$router.push({ name: 'props' })
        }
      } else {
        this.sessionVerified = false
      }
    })
  },
}
</script>


<style>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html,
body,
#app {
  height: 100%;
  overflow: hidden;
}

body {
  font-family: "Segoe UI", "Helvetica Neue", Montserrat, sans-serif;
  font-size: 15px;
  color: var(--admin-text);
  background: var(--admin-bg);
}

.page-hero h1 {
  letter-spacing: -0.04em;
}

.close_icon,
.detail_icon,
.plus_icon,
.reload_icon,
.delete_icon,
.list_icon,
.edit_icon {
  width: 32px;
  height: 32px;
  cursor: pointer;
  opacity: .6;
  background: url('~@/assets/edit.png') no-repeat center / 90%;
}

.delete_icon {
  background-image: url('~@/assets/delete.png');
}

.plus_icon {
  background-image: url('~@/assets/plus.png');
}

.reload_icon {
  background-image: url('~@/assets/reload.png');
}

.detail_icon {
  width: 16px;
  height: 16px;
  margin-left: 5px;
  background-image: url('~@/assets/info.png');
}

.close_icon {
  width: 16px;
  height: 16px;
  margin-left: 5px;
  background-image: url('~@/assets/close.png');
}

@keyframes spin {
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}

.reload_icon.active {
  animation: spin 1s linear infinite;
}

.list_icon {
  background-image: url('~@/assets/list.png');
}

.close_icon:hover,
.detail_icon:hover,
.plus_icon:hover,
.reload_icon:hover,
.list_icon:hover,
.delete_icon:hover,
.edit_icon:hover {
  opacity: 1;
}

.btn {
  padding: 8px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  background: #fff;
  color: #111827;
  font-weight: 600;
  font-size: 13px;
}

.btn:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

*.noSelect {
  -moz-user-select: none;
  -khtml-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.gla-auth-login__stack .gla-auth-login__intro,
.gla-auth-login__stack .gla-auth-login__buttons {
  width: 100%;
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
}

.auth-boot {
  height: 100vh;
  background: var(--admin-bg);
}
</style>
